/**
 * The shared WoW-style tooltip: one DOM element reused everywhere. Follows the cursor with edge
 * flipping, shows instantly on hover, hides on leave/scroll/wheel, and re-reads its model a few
 * times a second so live values (remaining time, cast progress) stay current. Holding Shift
 * switches to the extended view (coefficients and formulas).
 */
import { placeTooltip } from './position';
import type { TooltipModel } from './types';
import './tooltip.css';

export type TooltipProvider = () => TooltipModel | null | undefined;

const REFRESH_MS = 200;

class TooltipView {
  private root: HTMLElement | null = null;
  private provider: TooltipProvider | null = null;
  private anchor: HTMLElement | null = null;
  private cursor = { x: 0, y: 0 };
  private shift = false;
  private key = '';
  private timer: ReturnType<typeof setInterval> | null = null;

  private ensure(): HTMLElement {
    if (this.root) return this.root;
    const root = document.createElement('div');
    root.className = 'ktip';
    root.setAttribute('role', 'tooltip');
    document.body.appendChild(root);
    this.root = root;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Shift' || this.shift === e.shiftKey) return;
      this.shift = e.shiftKey;
      if (this.provider) this.refresh();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    window.addEventListener('blur', () => {
      this.shift = false;
      this.hide();
    });
    window.addEventListener('scroll', () => this.hide(), true);
    window.addEventListener('wheel', () => this.hide(), { capture: true, passive: true });
    return root;
  }

  show(anchor: HTMLElement, provider: TooltipProvider, x: number, y: number, shift = false): void {
    this.ensure();
    this.anchor = anchor;
    this.provider = provider;
    this.cursor = { x, y };
    this.shift = shift;
    this.key = '';
    this.refresh();
    if (!this.timer) this.timer = setInterval(() => this.refresh(), REFRESH_MS);
  }

  move(anchor: HTMLElement, x: number, y: number, shift: boolean): void {
    if (this.anchor !== anchor) return;
    this.cursor = { x, y };
    if (shift !== this.shift) {
      this.shift = shift;
      this.refresh();
    } else this.position();
  }

  hide(anchor?: HTMLElement): void {
    if (anchor && this.anchor !== anchor) return;
    this.provider = null;
    this.anchor = null;
    this.key = '';
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.root) this.root.classList.remove('show');
  }

  private refresh(): void {
    const root = this.root;
    if (!root || !this.provider) return;
    if (this.anchor && !this.anchor.isConnected) return this.hide();
    let model: TooltipModel | null | undefined = null;
    try {
      model = this.provider();
    } catch {
      model = null;
    }
    if (!model) {
      root.classList.remove('show');
      this.key = '';
      return;
    }
    const key = `${this.shift ? 1 : 0}${JSON.stringify(model)}`;
    if (key !== this.key) {
      this.key = key;
      renderModel(root, model, this.shift);
    }
    root.classList.add('show');
    this.position();
  }

  private position(): void {
    const root = this.root;
    if (!root || !root.classList.contains('show')) return;
    const r = root.getBoundingClientRect();
    const p = placeTooltip(this.cursor, { w: r.width, h: r.height }, { w: window.innerWidth, h: window.innerHeight });
    root.style.transform = `translate(${Math.round(p.x)}px, ${Math.round(p.y)}px)`;
  }
}

function div(cls: string, parent: HTMLElement, text?: string): HTMLDivElement {
  const d = document.createElement('div');
  d.className = cls;
  if (text !== undefined) d.textContent = text;
  parent.appendChild(d);
  return d;
}

/** Numbers (with their unit) in descriptions stand out: "Deals <b>4,600</b> Nature damage every <b>2 sec</b>". */
const NUMBER_RE = /([+−-]?\d[\d,]*(?:\.\d+)?(?:%|×)?(?: (?:sec|min|yd)\b)?)/g;

/** Fill `el` with `text`, wrapping numbers in `.ktip-num` spans (text nodes only, no HTML parsing). */
export function richText(el: HTMLElement, text: string): void {
  let last = 0;
  for (const m of text.matchAll(NUMBER_RE)) {
    const i = m.index ?? 0;
    if (i > last) el.appendChild(document.createTextNode(text.slice(last, i)));
    const span = document.createElement('span');
    span.className = 'ktip-num';
    span.textContent = m[0];
    el.appendChild(span);
    last = i + m[0].length;
  }
  if (last < text.length) el.appendChild(document.createTextNode(text.slice(last)));
}

function initials(name: string): string {
  return name
    .split(/[\s'-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/** Draw a model into the tooltip element (exported for screens that host their own panel). */
export function renderModel(root: HTMLElement, m: TooltipModel, shift: boolean): void {
  root.replaceChildren();
  root.dataset['kind'] = m.kind;
  const head = div('ktip-head', root);
  if (m.kind === 'ability') {
    const icon = div('ktip-icon', head);
    if (m.icon) {
      const img = document.createElement('img');
      img.src = m.icon;
      img.alt = '';
      icon.appendChild(img);
    } else {
      icon.textContent = initials(m.title);
      if (m.iconColor) icon.style.setProperty('--ktip-icon', m.iconColor);
    }
  }
  const titles = div('ktip-titles', head);
  div(`ktip-title ${m.titleTone ?? 'default'}`, titles, m.title);
  if (m.tag) div('ktip-tag', titles, m.tag);
  if (m.grid.length) {
    const grid = div('ktip-grid', root);
    for (const [l, r] of m.grid) {
      div('ktip-l', grid, l);
      div('ktip-r', grid, r);
    }
  }
  for (const line of m.lines) {
    if (line.tone === 'desc' || line.tone === 'talent') richText(div(`ktip-line tone-${line.tone}`, root), line.text);
    else div(`ktip-line tone-${line.tone}`, root, line.text);
  }
  if (m.extended.length) {
    if (shift) {
      div('ktip-sep', root);
      for (const line of m.extended) div(`ktip-line tone-${line.tone} small`, root, line.text);
    } else div('ktip-hint', root, 'Hold Shift for details');
  }
}

let shared: TooltipView | null = null;
/** The single shared tooltip instance. */
export function tooltip(): TooltipView {
  return (shared ??= new TooltipView());
}

export function hideAbilityTooltip(): void {
  shared?.hide();
}

const attached = new WeakMap<HTMLElement, () => void>();

/**
 * Show the shared tooltip while hovering `el`. `getModel` is re-evaluated on show and a few
 * times a second while visible (return null to show nothing). Returns a detach function;
 * attaching again replaces the previous provider.
 */
export function attachAbilityTooltip(el: HTMLElement, getModel: TooltipProvider): () => void {
  attached.get(el)?.();
  const enter = (e: MouseEvent): void => tooltip().show(el, getModel, e.clientX, e.clientY, e.shiftKey);
  const move = (e: MouseEvent): void => tooltip().move(el, e.clientX, e.clientY, e.shiftKey);
  const leave = (): void => tooltip().hide(el);
  el.addEventListener('mouseenter', enter);
  el.addEventListener('mousemove', move);
  el.addEventListener('mouseleave', leave);
  const detach = (): void => {
    el.removeEventListener('mouseenter', enter);
    el.removeEventListener('mousemove', move);
    el.removeEventListener('mouseleave', leave);
    tooltip().hide(el);
    attached.delete(el);
  };
  attached.set(el, detach);
  return detach;
}
